import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatJMD } from "@/lib/format";

export function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, subtotal, lineKey } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setDrawerOpen(false);
    navigate("/checkout");
  };
  const goCart = () => {
    setDrawerOpen(false);
    navigate("/cart");
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-md bg-background border-l border-white/10 flex flex-col p-0"
      >
        <SheetHeader className="px-5 py-4 border-b border-white/10">
          <SheetTitle className="font-display text-2xl tracking-wider text-left">
            YUH BAG <span className="text-jamaica-gold">({items.length})</span>
          </SheetTitle>
          <SheetDescription className="sr-only">Your shopping bag items and checkout</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-16 text-zinc-500">
              <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-mono text-xs uppercase tracking-widest">Bag empty. Nuh worry.</p>
            </div>
          )}
          {items.map((i) => {
            const key = lineKey(i);
            return (
              <div key={key} data-testid={`cart-line-${key}`} className="flex gap-3 border-b border-white/5 pb-4">
                <img src={i.image} alt={i.title} className="w-16 h-20 object-cover bg-zinc-900" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-heading font-bold uppercase text-xs leading-tight truncate">{i.title}</h4>
                  <p className="font-mono text-[10px] text-zinc-500 uppercase mt-0.5">
                    {i.size} · {i.color}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      data-testid={`cart-dec-${key}`}
                      onClick={() => updateQty(key, i.qty - 1)}
                      className="w-6 h-6 border border-border flex items-center justify-center hover:border-jamaica-gold"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="font-mono text-xs w-5 text-center">{i.qty}</span>
                    <button
                      data-testid={`cart-inc-${key}`}
                      onClick={() => updateQty(key, i.qty + 1)}
                      className="w-6 h-6 border border-border flex items-center justify-center hover:border-jamaica-gold"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                    <button
                      data-testid={`cart-remove-${key}`}
                      onClick={() => removeItem(key)}
                      className="ml-auto text-zinc-500 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="font-mono text-xs text-jamaica-gold">{formatJMD(i.price_cents * i.qty)}</div>
              </div>
            );
          })}
        </div>

        {items.length > 0 && (
          <div className="border-t border-white/10 px-5 py-4 space-y-3">
            <div className="flex justify-between font-mono text-sm">
              <span className="text-zinc-400 uppercase tracking-widest text-xs">Subtotal</span>
              <span className="text-jamaica-gold" data-testid="cart-subtotal">{formatJMD(subtotal)}</span>
            </div>
            <Button
              data-testid="drawer-checkout-btn"
              onClick={goCheckout}
              className="w-full py-6 bg-jamaica-gold hover:bg-yellow-300 text-black font-bold uppercase tracking-widest rounded-none"
            >
              Checkout
            </Button>
            <button
              data-testid="drawer-viewcart-btn"
              onClick={goCart}
              className="w-full py-2 font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-white"
            >
              View full bag
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
